// @ts-nocheck
import { useEffect, useRef, useState, Ref, useMemo, useCallback } from "react";
import { useNavigationStore } from "src/store/navigationStore";
import { Canvas, useFrame, RootState } from "@react-three/fiber";
import { Splat, useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { Group } from "three";
import { DEFAULT_AVATAR } from "src/const/navigationConst";
import { useModelViewerStore } from "@/store/modelViewerStore";
import {
  roundToDecimals,
  vec3Distance,
  hexToRgbArray,
  getYAngleDegree,
  angleDifferenceABtoAC,
  wrapAngle,
  calculateTargetPosition,
  normalizeRotation,
} from "src/utils/game";
import * as pc from "playcanvas";
import { OrbitControls } from "@playcanvas/react/scripts";
import {
  useTexture,
  useMaterial,
  useEnvAtlas,
  useApp,
  useSplat,
  useModel,
  useAppEvent,
} from "@playcanvas/react/hooks";
import { Application, Entity, Gltf } from "@playcanvas/react";
import {
  Render,
  Camera,
  Light,
  GSplat,
  Script,
  Environment,
} from "@playcanvas/react/components";
import { CameraControls } from "playcanvas/scripts/esm/camera-controls.mjs";
import WatermarkScreen from "src/components/watermark";
import { useLodSplat } from "src/utils/useLodSplat";
import LottieLoading from "src/components/lottie/lottie-loading";
import { GsplatRevealRadial } from "src/extra_js/reveal-radial.mjs";

export const createRadialScript = (
  mainObject,
  gsplatEntity,
  isReverse = false,
) => {
  const startPosition = mainObject.getPosition();
  const script = gsplatEntity.script?.create(GsplatRevealRadial);
  if (script) {
    if (isReverse) {
      script.center.set(startPosition.x, startPosition.y, -startPosition.z);
    } else {
      script.center.set(startPosition.x, startPosition.y, startPosition.z);
    }
    script.speed = 15;
    script.acceleration = 0;
    script.delay = 2;
    script.dotTint.set(0, 1, 1);
    script.waveTint.set(0, 1, 0);
    script.oscillationIntensity = 0.5;
    script.endRadius = 10000000;
  }
  return script;
};

export const createRadialScriptFast = (mainObject, gsplatEntity) => {
  const startPosition = mainObject.getPosition();
  const script = gsplatEntity.script?.create(GsplatRevealRadial);
  if (script) {
    script.center.set(startPosition.x, startPosition.y, startPosition.z);
    script.speed = 30;
    script.acceleration = 0;
    script.delay = 0.01;
    script.dotTint.set(0, 1, 1);
    script.waveTint.set(0, 1, 0);
    script.oscillationIntensity = 0.5;
    script.endRadius = 10000000;
  }
  return script;
};

export const GlbModel = ({ app, url }) => {
  const entityRef = useRef();
  const { asset: model } = useModel(url);
  const changeMaterial = (entity) => {
    if (entity == null) {
      return;
    }
    const renders = entity.findComponents("render");
    renders.forEach((render) => {
      if (render.meshInstances) {
        render.meshInstances.forEach((meshInstance) => {
          const material = meshInstance.material;
          meshInstance.material = material;
          if (material) {
            material.emissiveMap = material.diffuseMap;
            material.emissiveIntensity = 1.0;
            material.emissive = new pc.Color(1, 1, 1);
            material.useLighting = false;
            material.update();
          }
        });
      }
    });
  };
  useEffect(() => {
    if (model && entityRef.current) {
      requestAnimationFrame(() => {
        changeMaterial(entityRef.current);
      });
    }
  }, [model]);
  return model ? (
    <Entity>
      <Entity ref={entityRef}>
        <Render type="asset" asset={model} />
      </Entity>
    </Entity>
  ) : (
    <></>
  );
};

export const SplatLodComponent = ({
  app,
  url,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
  lodDistances = 1,
  callbackReady,
}) => {
  const entityRef = useRef(null);
  useEffect(() => {
    let disposed = false;
    const loadAsset = async () => {
      const asset = new pc.Asset("lod-splat", "gsplat", {
        url,
      });
      app.assets.add(asset);
      asset.ready(() => {
        setTimeout(() => {
          if (disposed || !entityRef.current) {
            return;
          }
          if (entityRef.current != null) {
            entityRef.current.gsplat.enabled = false;
            entityRef.current.gsplat.asset = asset;
            entityRef.current.gsplat.lodBaseDistance = 20 * lodDistances;
            entityRef.current.gsplat.lodMultiplier = 4;
            entityRef.current.addComponent("script");
            if (callbackReady) {
              callbackReady(entityRef.current, asset);
            }
          }
        }, 10);
      });
      app.assets.load(asset);
    };
    if (!app || !url) return;
    //let disposed = false;
    let entity = entityRef.current;
    if (entity) {
      entity.addComponent("gsplat", {
        unified: true,
      });
      entity.setLocalPosition(...position);
      entity.setLocalEulerAngles(...rotation);
      entity.setLocalScale(...scale);
      loadAsset();
    }
    return () => {
      disposed = true;
      if (entityRef.current) {
        entityRef.current.destroy();
        entityRef.current = null;
      }
    };
  }, [app, url]);
  useEffect(() => {
    if (!entityRef.current) return;
    entityRef.current.setLocalPosition(...position);
    entityRef.current.setLocalEulerAngles(...rotation);
    entityRef.current.setLocalScale(...scale);
  }, [position, rotation, scale]);

  useEffect(() => {
    if (!entityRef.current?.gsplat) return;
    entityRef.current.gsplat.lodDistances = lodDistances;
    //app.scene.gsplat.colorizeLod = true;
  }, [lodDistances]);

  return (
    <Entity
      ref={entityRef}
      name="lod-splat"
      position={position}
      rotation={rotation}
      scale={scale}
    ></Entity>
  );
};

export const SplatSogComponent = ({
  app,
  url,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
  callbackReady,
}) => {
  const entityRef = useRef(null);
  useEffect(() => {
    let disposed = false;
    const loadAsset = async () => {
      const asset = new pc.Asset("sog-splat", "gsplat", {
        url,
      });
      app.assets.add(asset);
      asset.ready(() => {
        setTimeout(() => {
          if (disposed || !entityRef.current) return;
          entityRef.current.gsplat.enabled = false;
          entityRef.current.gsplat.asset = asset;
          entityRef.current.addComponent("script");
          if (callbackReady) {
            callbackReady(entityRef.current);
          }
        }, 10);
      });
      app.assets.load(asset);
    };
    if (!app || !url) return;
    let entity = entityRef.current;
    if (entity) {
      entity.addComponent("gsplat", {
        unified: false,
        highQualitySH: true,
      });
      entity.setLocalPosition(...position);
      entity.setLocalEulerAngles(...rotation);
      entity.setLocalScale(...scale);
      loadAsset();
    }
    return () => {
      disposed = true;
      if (entityRef.current) {
        entityRef.current.destroy();
        entityRef.current = null;
      }
    };
  }, [app, url]);

  useEffect(() => {
    if (!entityRef.current) return;
    entityRef.current.setLocalPosition(...position);
    entityRef.current.setLocalEulerAngles(...rotation);
    entityRef.current.setLocalScale(...scale);
  }, [position, rotation, scale]);

  return (
    <Entity
      ref={entityRef}
      name="sog-splat"
      position={position}
      rotation={rotation}
      scale={scale}
    ></Entity>
  );
};

export const MeshModelComponent = ({
  app,
  cameraName,
  meshUrl,
  meshPosition,
  meshRotationAngle,
  meshScale,
  meshColor,
  meshOpacity,
  rotateObjects,
}: {
  app: any;
  cameraName: string;
  meshUrl: string;
  meshPosition: number[];
  meshRotationAngle: number[];
  meshScale: number[];
  meshColor: string;
  meshOpacity: number;
  rotateObjects?: string[];
}) => {
  const meshEntityRef = useRef();
  const { asset: meshAsset } = useModel(meshUrl);
  const color = hexToRgbArray(meshColor);
  let mainCamera = app.root.findByName(cameraName);
  const handleUpdate = useCallback((dt: number) => {
    if (rotateObjects == null) {
      return;
    }
    rotateObjects.forEach((eachObject) => {
      if (mainCamera == null) {
        mainCamera = app.root.findByName(cameraName);
      }
      const childEntity = meshEntityRef.current.findByName(eachObject);
      if (childEntity && mainCamera) {
        const rotationY = getYAngleDegree(
          childEntity.getPosition(),
          mainCamera.getPosition(),
        );
        childEntity.setEulerAngles(0, -rotationY, 0);
      }
    });
  }, []);
  useAppEvent("update", handleUpdate);

  const changeMaterial = (entity) => {
    if (entity == null) {
      return;
    }
    const renders = entity.findComponents("render");
    renders.forEach((render) => {
      if (render.meshInstances) {
        render.meshInstances.forEach((meshInstance) => {
          const material = meshInstance.material;
          if (material) {
            material.emissiveMap = material.diffuseMap;
            material.emissiveIntensity = 1.0;
            material.emissive = new pc.Color(color[0], color[1], color[2]);
            material.useLighting = false;
            material.update();
          }
        });
      }
    });
  };
  useEffect(() => {
    if (meshAsset && meshEntityRef.current) {
      requestAnimationFrame(() => {
        changeMaterial(meshEntityRef.current);
      });
    }
  }, [meshAsset]);

  return (
    <Entity
      ref={meshEntityRef}
      position={meshPosition}
      rotation={meshRotationAngle}
      scale={meshScale}
    >
      <Render type="asset" asset={meshAsset} />
    </Entity>
  );
};

export const RobotAvatar = ({avatarUrl = DEFAULT_AVATAR, isFloating = true}:{avatarUrl:string, isFloating:boolean}) => {
  const app = useApp();
  const entityRef = useRef();
  let floatTime = 0;
  const _avatarPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
  const { asset: robot } = useModel(avatarUrl);
  const changeMaterial = (entity) => {
    if (entity == null) {
      return;
    }
    const renders = entity.findComponents("render");
    renders.forEach((render) => {
      if (render.meshInstances) {
        render.meshInstances.forEach((meshInstance) => {
          const material = meshInstance.material;
          if (material) {
            if (material.diffuseMap) {
              material.diffuseMap.minFilter = pc.FILTER_LINEAR_MIPMAP_LINEAR;
              material.diffuseMap.magFilter = pc.FILTER_LINEAR;
              material.diffuseMap.anisotropy = 8;
              material.diffuseMap.mipmaps = true;
              material.diffuseMap.upload();
            }

            material.emissiveMap = material.diffuseMap;
            material.emissiveIntensity = 1.0;
            material.emissive = new pc.Color(1, 1, 1);
            material.useLighting = false;
            material.update();
          }
        });
      }
    });
  };
  useEffect(() => {
    if (robot && entityRef.current) {
      requestAnimationFrame(() => {
        changeMaterial(entityRef.current);
      });
    }
  }, [robot]);

  const handleUpdate = useCallback((dt: number) => {
    floatTime += dt;
    if (entityRef.current && isFloating) {
      _avatarPositionRef.current.set(0, Math.sin(floatTime * 2) * 0.05, 0);
      entityRef.current.setLocalPosition(_avatarPositionRef.current);
    }
  }, []);
  useAppEvent("update", handleUpdate);

  return robot ? (
    <Entity position={[0, -1, 0]}>
      <Entity
        ref={entityRef}
        rotation={[isFloating ? 10 : 0 , 180, 0]}
        position={[0, 0, 0]}
        scale={[1, 1, 1]}
      >
        <Render type="asset" asset={robot} />
      </Entity>
    </Entity>
  ) : (
    <></>
  );
};

export const FloorMeshComponent = ({
  floorMeshUrl,
  floorMeshPosition,
  floorMeshScale,
}: {
  floorMeshUrl: string;
  floorMeshPosition: pc.Vec3;
  floorMeshScale: pc.Vec3;
}) => {
  const floorMeshEntityRef = useRef();
  const { asset: floorMeshAsset } = useModel(floorMeshUrl);

  const changeMaterial = (entity) => {
    if (entity == null) {
      return;
    }
    const renders = entity.findComponents("render");
    renders.forEach((render) => {
      if (render.meshInstances) {
        render.meshInstances.forEach((meshInstance) => {
          const material = meshInstance.material;
          if (material) {
            material.emissiveMap = material.diffuseMap;
            material.emissiveIntensity = 1.0;
            material.emissive = new pc.Color(1, 1, 1);
            material.useLighting = false;
            material.update();
          }
        });
      }
    });
  };
  useEffect(() => {
    if (floorMeshAsset && floorMeshEntityRef.current) {
      requestAnimationFrame(() => {
        changeMaterial(floorMeshEntityRef.current);
      });
    }
  }, [floorMeshAsset]);

  return (
    <Entity
      ref={floorMeshEntityRef}
      rotation={[0, 180, 0]}
      position={floorMeshPosition}
      scale={floorMeshScale}
    >
      <Render type="asset" asset={floorMeshAsset} />
    </Entity>
  );
};

export const installCameraNearFar = (
  app: pc.Application,
  gsplatEntity: pc.Entity,
  hiddenNearEntity: pc.Entity,
  hiddenFarEntity: pc.Entity,
  width: number = 0.8,   // Controls Left-Right width perpendicular to camera direction
  height: number = 1,  // Controls Up-Down height
  feather: number = 0.1  // Soft edge transition
) => {
  const gsplatMat = (app.scene.gsplat as any)?.material || gsplatEntity.gsplat?.material;
  if (!gsplatMat) {
    console.warn("GSplat Material not found");
    return () => {};
  }

  if (!hiddenNearEntity || !hiddenFarEntity) return () => {};

  const glsl = `
    uniform mat4 uWorldToBox;   // World space to Box local space
    uniform vec3 uBoxHalfSize;  // vec3(halfWidth, halfHeight, halfDepth)
    uniform float uCutFeather;

    float getBoxAlpha(vec3 localPos) {
      vec3 d = abs(localPos) - uBoxHalfSize;
      float maxDist = max(d.x, max(d.y, d.z));
      return smoothstep(0.0, uCutFeather, maxDist);
    }

    void modifySplatCenter(inout vec3 center) {
    }

    void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
      vec4 localPos = uWorldToBox * vec4(originalCenter, 1.0);
      float alpha = getBoxAlpha(localPos.xyz);
      scale *= alpha;
    }

    void modifySplatColor(vec3 center, inout vec4 color) {
      vec4 localPos = uWorldToBox * vec4(center, 1.0);
      float alpha = getBoxAlpha(localPos.xyz);
      color.a *= alpha;
    }
  `;

  const identityMat = new pc.Mat4();
  gsplatMat.setParameter("uWorldToBox", identityMat.data);
  gsplatMat.setParameter("uBoxHalfSize", [width * 0.5, height * 0.5, 1.0]);
  gsplatMat.setParameter("uCutFeather", feather);

  gsplatMat.getShaderChunks("glsl").set("gsplatModifyVS", glsl);
  gsplatMat.update();

  const nearPos = new pc.Vec3();
  const farPos = new pc.Vec3();
  const centerPos = new pc.Vec3();
  const dirVec = new pc.Vec3();
  const boxTransform = new pc.Mat4();
  const worldToBox = new pc.Mat4();

  const onUpdate = () => {
    if (!hiddenNearEntity || !hiddenFarEntity) return;

    nearPos.copy(hiddenNearEntity.getPosition());
    farPos.copy(hiddenFarEntity.getPosition());
    dirVec.sub2(farPos, nearPos);
    const distanceBetweenEntities = dirVec.length();
    if (distanceBetweenEntities < 0.0001) return;

    // Center point halfway between near and far entities
    centerPos.add2(nearPos, farPos).mulScalar(0.5);

    // Build transformation matrix pointing from Near to Far along local Z-axis
    // Using (nearPos -> farPos) ensures local Z runs along the line of sight
    boxTransform.setLookAt(nearPos, farPos, pc.Vec3.UP);
    
    // Invert to get World-to-Local matrix
    worldToBox.copy(boxTransform).invert();

    gsplatMat.setParameter("uWorldToBox", worldToBox.data);
    
    // Correct mapping:
    // X = width (Horizontal span across camera view)
    // Y = height (Vertical span)
    // Z = distance strictly between near & far entities
    gsplatMat.setParameter("uBoxHalfSize", [
      width * 0.5,                   // Local X = Width
      height * 0.5,                  // Local Y = Height
      distanceBetweenEntities * 0.5  // Local Z = Depth between Near & Far
    ]);
  };
  onUpdate();
  app.on("update", onUpdate);
  return () => app.off("update", onUpdate);
};

// export const installCameraNearFar = (
//   app: pc.Application,
//   gsplatEntity: pc.Entity,
//   hiddenNearEntity: pc.Entity,
//   hiddenFarEntity: pc.Entity,
// ) => {
//   const device = app.graphicsDevice;
//   const gsplatMat = app.scene.gsplat.material || gsplatEntity.gsplat?.material;
//   if (!gsplatMat) return () => {};

//   if (!hiddenNearEntity || !hiddenFarEntity) {
//     return () => {};
//   }

//   const glsl = `
//       uniform vec3 uCutA;       // camera position in WORLD space
//       uniform vec3 uCutB;       // robot position in WORLD space
//       uniform float uCutRadius; // cylinder radius of the cutaway
//       uniform float uCutFeather;// soft edge transition distance

//       // Calculate the shortest distance from a point (p) to a line segment (a to b)
//       float segDist(vec3 p, vec3 a, vec3 b) {
//         vec3 pa = p - a;
//         vec3 ba = b - a;
//         float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-6), 0.0, 1.0);
//         return length(pa - ba * h);
//       }

//       void modifySplatCenter(inout vec3 center) {
//       }

//       void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
//         // Calculate distance between current splat and the Camera-Robot line segment
//         float d = segDist(originalCenter, uCutA, uCutB);
//         float alpha = smoothstep(uCutRadius, uCutRadius + uCutFeather, d);

//         // Scale down splats that are too close to the line to 0, making them disappear
//         scale *= alpha;
//       }

//       void modifySplatColor(vec3 center, inout vec4 color) {
//         // Apply the same transition to the alpha channel as a double-safeguard
//         float d = segDist(center, uCutA, uCutB);
//         float alpha = smoothstep(uCutRadius, uCutRadius + uCutFeather, d);
//         color.a *= alpha;
//       }
//     `;

//   gsplatMat.getShaderChunks("glsl").set("gsplatModifyVS", glsl);
//   gsplatMat.update();

//   const uCutA = device.scope.resolve("uCutA");
//   const uCutB = device.scope.resolve("uCutB");
//   const uCutRadius = device.scope.resolve("uCutRadius");
//   const uCutFeather = device.scope.resolve("uCutFeather");
//   uCutRadius.setValue(1);
//   uCutFeather.setValue(0.01);

//   const onUpdate = () => {
//     if (!hiddenNearEntity || !hiddenFarEntity) {
//       return;
//     }
//     const nearPosition = hiddenNearEntity.getPosition().clone();
//     const farPosition = hiddenFarEntity.getPosition().clone();
//     if (nearPosition.length() === 0 || isNaN(nearPosition.x)) return;
//     const dirToCamera = new pc.Vec3().sub2(nearPosition, farPosition);
//     const totalDist = dirToCamera.length();
//     dirToCamera.normalize();
//     uCutA.setValue([nearPosition.x, nearPosition.y, nearPosition.z]);
//     uCutB.setValue([farPosition.x, farPosition.y, farPosition.z]);
//   };
//   app.on("update", onUpdate);
//   return () => app.off("update", onUpdate);
// };
